import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { Button } from '@/components/ui'
import type { ResolutionType } from '@/types'

/**
 * Plain-speech resolution options from the iOS AlertDetail sheet,
 * mapped to the backend ResolutionType values.
 */
const RESOLUTION_OPTIONS: { value: ResolutionType; label: string }[] = [
  { value: 'FALSE_POSITIVE', label: 'Looks fine — false alarm' },
  { value: 'ACTION_TAKEN', label: 'I handled it' },
  { value: 'NO_ACTION_NEEDED', label: 'Real, but no action needed' },
  { value: 'CONFIRMED', label: 'Confirmed issue' },
]

interface ResolveAlertDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Title of the alert being resolved, shown for context */
  alertTitle?: string
  onResolve: (resolution: ResolutionType) => void
  isResolving?: boolean
  error?: string | null
}

/**
 * Hearth resolve sheet: picking an option resolves the alert immediately,
 * mirroring the mobile app's one-tap resolution flow.
 */
export function ResolveAlertDialog({
  open,
  onOpenChange,
  alertTitle,
  onResolve,
  isResolving = false,
  error,
}: ResolveAlertDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] rounded-2xl bg-surface p-6 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          <Dialog.Title className="text-lg font-semibold text-gray-900">
            How did this turn out?
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-sm text-gray-500">
            {alertTitle
              ? `Let us know how "${alertTitle}" was handled so we can close it out.`
              : 'Let us know how this alert was handled so we can close it out.'}
          </Dialog.Description>

          {error && <p role="alert" className="mt-3 text-red-700">{error}</p>}
          <div className="mt-5 space-y-2">
            {RESOLUTION_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                disabled={isResolving}
                onClick={() => onResolve(option.value)}
                className="w-full rounded-xl bg-gray-100 px-4 py-3 text-left text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 disabled:pointer-events-none disabled:opacity-50"
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="mt-5 flex justify-end">
            <Dialog.Close asChild>
              <Button variant="ghost" disabled={isResolving}>
                Cancel
              </Button>
            </Dialog.Close>
          </div>

          <Dialog.Close asChild>
            <button
              className="absolute right-4 top-4 rounded-sm text-gray-500 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
